export type OrganizationRoleDto = {
  id: number;
  name: string;
  description: string | null;
  organizationId: number;
};

export type OrganizationUserDto = {
  id: number;
  email: string;
  fullname: string | null;
  organizationId: number;
  roles: OrganizationRoleDto[];
};

class AdminApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl =
      (process.env.REACT_APP_SOCKET_URL || "http://localhost:5150") + "/api/admin";
  }

  private getToken(): string {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.token || "";
      } catch {
        return "";
      }
    }
    return "";
  }

  private async request<T>(
    method: string,
    path: string,
    data?: unknown
  ): Promise<T> {
    const token = this.getToken();
    const options: RequestInit = {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    if (data && ["POST", "PUT", "PATCH"].includes(method)) {
      options.body = JSON.stringify(data);
    }
    const response = await fetch(`${this.baseUrl}${path}`, options);
    const json = await response.json();
    if (!response.ok || (json.status != null && json.status >= 400)) {
      throw new Error(json.error || json.message || "Request failed");
    }
    return json.data as T;
  }

  getRoles(): Promise<OrganizationRoleDto[]> {
    return this.request<OrganizationRoleDto[]>("GET", "/roles");
  }

  createRole(body: {
    name: string;
    description?: string | null;
  }): Promise<OrganizationRoleDto> {
    return this.request<OrganizationRoleDto>("POST", "/roles", body);
  }

  updateRole(
    id: number,
    body: { name?: string; description?: string | null }
  ): Promise<OrganizationRoleDto> {
    return this.request<OrganizationRoleDto>("PATCH", `/roles/${id}`, body);
  }

  deleteRole(id: number): Promise<{ id: number }> {
    return this.request<{ id: number }>("DELETE", `/roles/${id}`);
  }

  getUsers(): Promise<OrganizationUserDto[]> {
    return this.request<OrganizationUserDto[]>("GET", "/users");
  }

  createUser(body: {
    email: string;
    password: string;
    fullname?: string | null;
  }): Promise<OrganizationUserDto> {
    return this.request<OrganizationUserDto>("POST", "/users", body);
  }

  setUserRoles(
    userId: number,
    roleIds: number[]
  ): Promise<OrganizationUserDto> {
    return this.request<OrganizationUserDto>(
      "PATCH",
      `/users/${userId}/roles`,
      { roleIds }
    );
  }
}

export default new AdminApi();
